import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FixtureStatus as CoreFixtureStatus } from '@sorare/core/src/components/lobby/FixtureStatus';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { getFixtureState } from 'lib/so5';

import { FixtureTimeline } from '../FixtureTimeline';
import type {
  FixtureStatus_composeTeamBenchObjectInterface,
  FixtureStatus_so5Fixture,
  FixtureStatus_so5Lineup,
} from './__generated__/index.graphql';

const DropdownContent = styled.div`
  position: relative;
  isolation: isolate;
  padding: var(--double-unit);
`;
const StyledIconButton = styled(IconButton)`
  position: absolute;
  z-index: 1;
  right: var(--unit);
  top: var(--unit);
`;

type Props = {
  so5Fixture: FixtureStatus_so5Fixture;
  so5Lineup?: FixtureStatus_so5Lineup;
  benchObjects?: FixtureStatus_composeTeamBenchObjectInterface[];
  onClick?: () => void;
  detailsOpen?: boolean;
  setDetailsOpen?: (value: boolean) => void;
};

export const FixtureStatus = ({
  setDetailsOpen,
  detailsOpen,
  so5Fixture,
  so5Lineup,
  benchObjects,
  onClick,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const fixtureState = getFixtureState(so5Fixture);

  if (!fixtureState) {
    return null;
  }

  return (
    <Dropdown
      align="right"
      open={detailsOpen}
      onOpen={() => {
        setDetailsOpen?.(true);
      }}
      triggerOnHover
      gap={8}
      label={({ onClick: onDropdownClick, ...props }) => (
        <ButtonBase
          {...props}
          onClick={e => {
            onDropdownClick?.(e);
            setDetailsOpen?.(true);
            onClick?.();
          }}
        >
          <CoreFixtureStatus
            fixtureState={fixtureState}
            startDate={so5Fixture.startDate}
            expectedRewardsDistributionDate={
              so5Fixture.expectedRewardsDistributionDate
            }
          />
        </ButtonBase>
      )}
      onClose={() => setDetailsOpen?.(false)}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {!isLaptopAndAbove && (
            <StyledIconButton
              size="medium"
              color="transparent"
              icon={faTimes}
              onClick={() => closeDropdown()}
            />
          )}
          <FixtureTimeline
            so5Fixture={so5Fixture}
            so5Lineup={so5Lineup}
            benchObjects={benchObjects}
          />
        </DropdownContent>
      )}
    </Dropdown>
  );
};

FixtureStatus.fragments = {
  so5Fixture: gql`
    fragment FixtureStatus_so5Fixture on So5Fixture {
      slug
      startDate
      expectedRewardsDistributionDate
      ...getFixtureState_so5Fixture
      ...FixtureTimeline_so5Fixture
    }
    ${getFixtureState.fragments.so5Fixture}
    ${FixtureTimeline.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureStatus_so5Fixture>,
  so5Lineup: gql`
    fragment FixtureStatus_so5Lineup on So5Lineup {
      id
      ...FixtureTimeline_so5Lineup
    }
    ${FixtureTimeline.fragments.so5Lineup}
  ` as TypedDocumentNode<FixtureStatus_so5Lineup>,
  composeTeamBenchObjectInterface: gql`
    fragment FixtureStatus_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      ...FixtureTimeline_composeTeamBenchObjectInterface
    }
    ${FixtureTimeline.fragments.composeTeamBenchObjectInterface}
  ` as TypedDocumentNode<FixtureStatus_composeTeamBenchObjectInterface>,
};
